import React, { useContext, useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { CredsContext } from "./Contexts";
import Tools from "./JNSTools";
import GlobalData from "./GlobalData";
import Box from "@mui/material/Box";
import Tour from "./Tour";
import ChangeLogByDateComponent from "./ChangeLogByDateComponent";
import ChangeLogByNameComponent from "./ChangeLogByNameComponent";
import ChangeLogHeader from "./ChangeLogHeader";
import "./styles/ChangeLog.css";

function ChangeLogContainer(props) {
  const creds = useContext(CredsContext).creds;
  const authString = Tools.getXmlBodyAuthString(creds);
  const urlString = Tools.getInstanceUrl(creds);
  const projectObject = JSON.parse(sessionStorage.getItem("projectObject"));
  const [open, setOpen] = useState();
  const [dirty, isDirty] = useState();
  const mainDataArray = useRef([]);
  const groupBy = useRef("date");
  const taskArray = useRef([]);
  const issueArray = useRef([]);
  const reqArray = useRef([]);
  const checkboxSettingsTasks = useRef();
  const checkboxSettingsIssues = useRef();
  const checkboxSettingsReqs = useRef();
  const rawChangeArray = useRef();
  const downloadFileTrigger = useRef(false);
  const DAYS_PAST = 14;
  const TOUR_ID = "Change Log";
  const initialTourState = {
    key: new Date(),
    run: false,
    loading: false,
    stepIndex: 0,
    steps: [
      {
        target: ".tour-stop-1",
        content: (
          <Box>
            <p>
              These checkboxes will filter the change log to show any
              combination of Tasks, RAID items, and Requirements
            </p>
          </Box>
        ),
        disableBeacon: true,
      },
      {
        target: ".tour-stop-2",
        content: (
          <Box>
            <p>
              Clicking the column header will sort the column. Clicking the icon
              on the column header will allow filtering on the column values.
            </p>
          </Box>
        ),
        disableBeacon: true,
      },
    ],
  };
  //let ISSUE_CATEGORY_ID = 5; // = 10; // change this to a lookup, 5 in sandbox
  //let REQUIREMENT_CATEGORY_ID = 6; // = 11; // change this to a lookup, 6 in sandbox

  let RISK_CATEGORY_ID;
  let ACTION_CATEGORY_ID;
  let ASSUMPTION_CATEGORY_ID;
  let ISSUE_CATEGORY_ID;
  let DECISION_CATEGORY_ID;
  let REQUIREMENT_CATEGORY_ID;

  const oadaSettingsRaw = localStorage.getItem("oadaSettings");
  if (oadaSettingsRaw) {
    const oadaSettings = JSON.parse(oadaSettingsRaw);
    RISK_CATEGORY_ID = oadaSettings["riskCategoryDropdown_options"];
    ACTION_CATEGORY_ID = oadaSettings["actionCategoryDropdown_options"];
    ASSUMPTION_CATEGORY_ID = oadaSettings["assumptionCategoryDropdown_options"];
    ISSUE_CATEGORY_ID = oadaSettings["issueCategoryDropdown_options"];
    DECISION_CATEGORY_ID = oadaSettings["decisionCategoryDropdown_options"];
    REQUIREMENT_CATEGORY_ID =
      oadaSettings["requirementCategoryDropdown_options"];
  }

  let settings;
  const settingsRaw = localStorage.getItem("oadaSettings");
  if (settingsRaw) {
    settings = JSON.parse(settingsRaw);
  }

  let settingsChanged = false;

  if (settings.changeLogCheckboxSettingsTasks !== undefined) {
    checkboxSettingsTasks.current = settings.changeLogCheckboxSettingsTasks;
  } else {
    checkboxSettingsTasks.current = true;
    settings.changeLogCheckboxSettingsTasks = true;
    settingsChanged = true;
  }
  if (settings.changeLogCheckboxSettingsIssues !== undefined) {
    checkboxSettingsIssues.current = settings.changeLogCheckboxSettingsIssues;
  } else {
    checkboxSettingsIssues.current = true;
    settings.changeLogCheckboxSettingsIssues = true;
    settingsChanged = true;
  }
  if (settings.changeLogCheckboxSettingsReqs !== undefined) {
    checkboxSettingsReqs.current = settings.changeLogCheckboxSettingsReqs;
  } else {
    checkboxSettingsReqs.current = true;
    settings.changeLogCheckboxSettingsReqs = true;
    settingsChanged = true;
  }

  if (settingsChanged) {
    localStorage.setItem("oadaSettings", JSON.stringify(settings));
    Tools.writeSettings(creds, projectObject.id);
  }

  const handleCheckboxChange = () => {
    isDirty(!dirty);
  };

  const handleClick = (event) => {
    let node = event.target;
    while (node.id == "") {
      node = node.parentNode;
    }
    const tier = node.id;
    let newOpen = open;
    newOpen[tier] = !newOpen[tier];
    setOpen(newOpen);
    isDirty(!dirty);
  };

  const handleGroupByChange = () => {
    if (groupBy.current == "date") {
      groupBy.current = "name";
    } else {
      groupBy.current = "date";
    }
    mainDataArray.current = [];
    isDirty(!dirty);
  };

  useEffect(() => {
    getRecords();
  }, [groupBy.current]);

  async function getRecords() {
    props.waitHandler(true);
    let initialOpen = {};
    let changeArray = [];

    const today = new Date();
    const pastTimestamp = today.getTime() - 86400000 * DAYS_PAST;

    today.setTime(pastTimestamp);
    const dateSegment =
      "<Date><year>" +
      today.getFullYear() +
      "</year><month>" +
      (today.getMonth() + 1) +
      "</month><day>" +
      today.getDate() +
      "</day><hour>00</hour><minute>00</minute><second>00</second></Date>";
    const filterSegment = ' filter="newer-than"';
    for (
      let recordTypeCounter = 0;
      recordTypeCounter < 3;
      recordTypeCounter++
    ) {
      if (recordTypeCounter == 0) {
        let requestString =
          authString +
          '<Read type="Projecttask" method="equal to" limit="1000" enable_custom="1"><Projecttask><projectid>' +
          projectObject.id +
          "</projectid></Projecttask></Read></request>";

        if (!downloadFileTrigger.current) {
          requestString = requestString.replace(
            " method=",
            filterSegment + " method=",
          );
          requestString = requestString.replace(
            "</Projecttask>",
            dateSegment + "</Projecttask>",
          );
        }

        if (taskArray.current.length == 0) {
          const taskRecs = await Tools.fetchPost(
            requestString,
            urlString,
            "Projecttask",
          );
          taskArray.current = Tools.xmlToArray(taskRecs.xml);
        }
        for (let i = 0; i < taskArray.current.length; i++) {
          const changeLogs =
            taskArray.current[i].oada_task_change_log__c.split("||");
          const taskName = taskArray.current[i].name;
          const taskIdNumber = taskArray.current[i].id_number;
          for (let j = 0; j < changeLogs.length; j++) {
            const logComponents = changeLogs[j].split("|");
            if (logComponents[0] != "") {
              if (logComponents.length == 2) {
                if (parseInt(logComponents[0]) >= pastTimestamp) {
                  changeArray.push({
                    dateInMillis: parseInt(logComponents[0]),
                    date: new Date(parseInt(logComponents[0])),
                    changeMessage: logComponents[1],
                    entityName: taskName,
                    entityType: "task",
                    entityId: taskIdNumber,
                  });
                }
              } else {
                console.log(
                  "error in ChangeLogContainer, taskName: " +
                    taskName +
                    ", logComponents: " +
                    JSON.stringify(logComponents),
                );
              }
            }
          }
        }
        //changeArray.pop();
      }

      if (recordTypeCounter == 1) {
        let requestString =
          authString +
          '<Read type="Issue" method="equal to" limit="1000" enable_custom="1"><Issue><project_id>' +
          projectObject.id +
          "</project_id><issue_category_id>" +
          RISK_CATEGORY_ID +
          "</issue_category_id></Issue></Read>" +
          '<Read type="Issue" method="equal to" limit="1000" enable_custom="1"><Issue><project_id>' +
          projectObject.id +
          "</project_id><issue_category_id>" +
          ASSUMPTION_CATEGORY_ID +
          "</issue_category_id></Issue></Read>" +
          '<Read type="Issue" method="equal to" limit="1000" enable_custom="1"><Issue><project_id>' +
          projectObject.id +
          "</project_id><issue_category_id>" +
          ISSUE_CATEGORY_ID +
          "</issue_category_id></Issue></Read>" +
          '<Read type="Issue" method="equal to" limit="1000" enable_custom="1"><Issue><project_id>' +
          projectObject.id +
          "</project_id><issue_category_id>" +
          DECISION_CATEGORY_ID +
          "</issue_category_id></Issue></Read></request>";

        if (!downloadFileTrigger.current) {
          requestString = requestString.replace(
            " method=",
            filterSegment + " method=",
          );
          requestString = requestString.replace(
            "</Issue>",
            dateSegment + "</Issue>",
          );
        }
        if (issueArray.current.length == 0) {
          const issueRecs = await Tools.fetchPost(
            requestString,
            urlString,
            "Issue",
          );
          issueArray.current = Tools.xmlToArray(issueRecs.xml);
        }
        for (let i = 0; i < issueArray.current.length; i++) {
          const changeLogs =
            issueArray.current[i].oada_issue_change_log__c.split("||");
          const issueName = issueArray.current[i].description; // + "|" + issueArray.current[i].name;
          const issueId = issueArray.current[i].name;

          for (let j = 0; j < changeLogs.length; j++) {
            const logComponents = changeLogs[j].split("|");
            if (logComponents[0] != "") {
              if (parseInt(logComponents[0]) >= pastTimestamp) {
                changeArray.push({
                  dateInMillis: parseInt(logComponents[0]),
                  date: new Date(parseInt(logComponents[0])),
                  changeMessage: logComponents[1],
                  entityName: issueName,
                  entityType: "issue",
                  entityId: issueId,
                });
              }
            }
          }
        }
        //changeArray.pop();
      }
      if (recordTypeCounter == 2) {
        let requestString =
          authString +
          '<Read type="Issue" method="equal to" limit="1000" enable_custom="1"><Issue><project_id>' +
          projectObject.id +
          "</project_id><issue_category_id>" +
          REQUIREMENT_CATEGORY_ID +
          "</issue_category_id></Issue></Read></request>";

        if (!downloadFileTrigger.current) {
          requestString = requestString.replace(
            " method=",
            filterSegment + " method=",
          );
          requestString = requestString.replace(
            "</Issue>",
            dateSegment + "</Issue>",
          );
        }
        if (reqArray.current.length == 0) {
          const reqRecs = await Tools.fetchPost(
            requestString,
            urlString,
            "Issue",
          );
          reqArray.current = Tools.xmlToArray(reqRecs.xml);
        }
        for (let i = 0; i < reqArray.current.length; i++) {
          const changeLogs =
            reqArray.current[i].oada_issue_change_log__c.split("||");
          const reqName = reqArray.current[i].description; // + "|" + reqArray.current[i].name;
          const reqId = reqArray.current[i].name;
          for (let j = 0; j < changeLogs.length; j++) {
            const logComponents = changeLogs[j].split("|");
            if (logComponents[0] != "") {
              if (parseInt(logComponents[0]) >= pastTimestamp) {
                changeArray.push({
                  dateInMillis: parseInt(logComponents[0]),
                  date: new Date(parseInt(logComponents[0])),
                  changeMessage: logComponents[1],
                  entityName: reqName,
                  entityType: "requirement",
                  entityId: reqId,
                });
              }
            }
          }
        }
        //changeArray.pop();
      }
    }

    rawChangeArray.current = changeArray;
    if (groupBy.current == "date") {
      changeArray.sort((a, b) => b.dateInMillis - a.dateInMillis);

      for (let x = 0; x < changeArray.length; x++) {
        const tempDate = new Date(changeArray[x].date);
        tempDate.setDate(tempDate.getDate() - tempDate.getDay());
        let monthIndex = mainDataArray.current.findIndex(
          (elem) => elem.month == tempDate.getMonth(),
        );
        if (monthIndex == -1) {
          monthIndex = mainDataArray.current.length;
          mainDataArray.current.push({
            month: tempDate.getMonth(),
            entityType: [],
            weeks: [],
          });
          initialOpen["m" + monthIndex] = true;
        }
        if (
          mainDataArray.current[monthIndex].entityType.indexOf(
            changeArray[x].entityType,
          ) == -1
        ) {
          mainDataArray.current[monthIndex].entityType.push(
            changeArray[x].entityType,
          );
        }
        let weekIndex = mainDataArray.current[monthIndex].weeks.findIndex(
          (elem) => elem.weekStarting == tempDate.getDate(),
        );
        if (weekIndex == -1) {
          weekIndex = mainDataArray.current[monthIndex].weeks.length;
          mainDataArray.current[monthIndex].weeks.push({
            weekStarting: tempDate.getDate(),
            entityType: [],
            days: [],
          });
          initialOpen["m" + monthIndex + "w" + weekIndex] = true;
        }
        if (
          mainDataArray.current[monthIndex].weeks[weekIndex].entityType.indexOf(
            changeArray[x].entityType,
          ) == -1
        ) {
          mainDataArray.current[monthIndex].weeks[weekIndex].entityType.push(
            changeArray[x].entityType,
          );
        }
        let dayIndex = mainDataArray.current[monthIndex].weeks[
          weekIndex
        ].days.findIndex((elem) => elem.date == changeArray[x].date.getDate());
        if (dayIndex == -1) {
          dayIndex =
            mainDataArray.current[monthIndex].weeks[weekIndex].days.length;
          mainDataArray.current[monthIndex].weeks[weekIndex].days.push({
            date: changeArray[x].date.getDate(),
            entityType: [],
            messages: [],
          });
          initialOpen["m" + monthIndex + "w" + weekIndex + "d" + dayIndex] =
            true;
        }

        if (
          mainDataArray.current[monthIndex].weeks[weekIndex].days[
            dayIndex
          ].entityType.indexOf(changeArray[x].entityType) == -1
        ) {
          mainDataArray.current[monthIndex].weeks[weekIndex].days[
            dayIndex
          ].entityType.push(changeArray[x].entityType);
        }
        mainDataArray.current[monthIndex].weeks[weekIndex].days[
          dayIndex
        ].messages.push({
          message: changeArray[x].changeMessage,
          entityName: changeArray[x].entityName,
          entityType: changeArray[x].entityType,
          entityId: changeArray[x].entityId,
        });
      }
    } else if (groupBy.current == "name") {
      changeArray.sort((a, b) => {
        a.entityName < b.entityName;
      });

      for (let y = 0; y < changeArray.length; y++) {
        let nameIndex = mainDataArray.current.findIndex(
          (elem) => elem.entityName == changeArray[y].entityName,
        );
        if (nameIndex == -1) {
          nameIndex = mainDataArray.current.length;
          mainDataArray.current.push({
            entityType: changeArray[y].entityType,
            entityName: changeArray[y].entityName,
            entityId: changeArray[y].entityId,
            messages: [],
          });
        }
        mainDataArray.current[nameIndex].messages.push(
          changeArray[y].changeMessage,
        );
        initialOpen["n" + nameIndex] = true;
      }
    }
    props.waitHandler(false);
    setOpen(initialOpen);
  }

  const handleNewWindow = (event) => {
    sessionStorage.setItem("projectObject", JSON.stringify(projectObject));
    const newWindow = window.open(
      "http://localhost:3000?target=Requirements", //&reqId=" +
      /*tableToOpen +
      "&recordid=" +
      recordId,*/
      "_blank",
    );
    newWindow.focus();
  };

  //<Tour initialState={initialTourState} id={TOUR_ID} />
  return groupBy.current == "date" ? (
    <Box>
      <div style={{ position: "fixed", width: "100%", zIndex: 999 }}>
        <ChangeLogHeader
          className="tour-stop-1"
          projectname={projectObject.name}
          handleGroupByChange={handleGroupByChange}
          handleCheckboxChange={handleCheckboxChange}
          daysPast={DAYS_PAST}
          groupBy="date"
        />
      </div>
      <br></br>

      <Box>
        <ChangeLogByDateComponent
          className="tour-stop-2"
          changeLog={mainDataArray.current}
          changeArray={rawChangeArray.current}
          projectname={projectObject.name}
          handleClick={handleClick}
          handleNewWindow={handleNewWindow}
          //handleGroupByChange={handleGroupByChange}
          //handleCheckboxChange={handleCheckboxChange}
          open={open}
        />
      </Box>
    </Box>
  ) : (
    <Box>
      <div style={{ position: "fixed", width: "100%", zIndex: 999 }}>
        <ChangeLogHeader
          projectname={projectObject.name}
          handleGroupByChange={handleGroupByChange}
          handleCheckboxChange={handleCheckboxChange}
          groupBy="name"
        />
      </div>
      <br></br>
      <ChangeLogByNameComponent
        changeLog={mainDataArray.current}
        projectname={projectObject.name}
        handleClick={handleClick}
        handleNewWindow={handleNewWindow}
        handleGroupByChange={handleGroupByChange}
        handleCheckboxChange={handleCheckboxChange}
        open={open}
      />
    </Box>
  );
}
export default ChangeLogContainer;

ChangeLogContainer.propTypes = {
  waitHandler: PropTypes.func,
};
